/**
 * @file NotificationTemplatePushTab.js
 * @description Push content tab for the multitenant notification template editor. Destination options are
 * restricted to Full Message, Home, and Website per the notification-template spec.
 */
import { CharacterLimitIndicator } from '../../helpers/CharacterLimitIndicator';
import RichTextEditor from '../../new-portal-page/RichTextEditor';
import { Dropdown } from '../../helpers/Dropdown';
import { htmlToPlainText } from '../../../utils/htmlText';
import {
    PushDestinationType,
    PushMenuDestination,
} from '../../../utils/data/enums';
import { getText } from '../../../i18n';

const PUSH_MAX_CHARS = 178;

const DESTINATION_OPTIONS = [
    {
        value: 'full-message',
        typeId: PushDestinationType.FullMessage,
        labelKey: 'msg_cb1d5ebbe195',
    },
    {
        value: 'home',
        typeId: PushDestinationType.Menu,
        labelKey: 'msg_2b3c4d5e6f7a',
    },
    {
        value: 'website',
        typeId: PushDestinationType.WebsiteUrl,
        labelKey: 'msg_2e8a57cc5c47',
    },
];

const fieldByTypeId = DESTINATION_OPTIONS.reduce((map, option) => {
    map[option.typeId] = option.value;
    return map;
}, {});

const NotificationTemplatePushTab = ({
    initialPushBody,
    currentPushBody,
    pushDestinationTypeId,
    pushDestinationValue,
    onPushBodyChange,
    onDestinationChange,
    isReadOnly,
    agencyId = null,
}) => {
    const destinationOptions = DESTINATION_OPTIONS.map(option => ({
        value: option.value,
        label: getText(option.labelKey),
    }));

    const selectedField =
        fieldByTypeId[pushDestinationTypeId] || 'full-message';

    const handleDestinationChange = field => {
        const option = DESTINATION_OPTIONS.find(
            candidate => candidate.value === field,
        );
        if (!option) return;

        if (field === 'home') {
            onDestinationChange(option.typeId, PushMenuDestination.Home);
            return;
        }

        if (field === 'full-message') {
            onDestinationChange(option.typeId, null);
            return;
        }

        onDestinationChange(
            option.typeId,
            selectedField === 'website' ? pushDestinationValue : '',
        );
    };

    return (
        <div>
            <div
                style={{
                    opacity: isReadOnly ? 0.6 : 1,
                    pointerEvents: isReadOnly ? 'none' : 'auto',
                }}
            >
                <RichTextEditor
                    initialContent={initialPushBody}
                    onContentUpdate={onPushBodyChange}
                    disableFontStyling={true}
                    disableParagraphStyling={true}
                    disableImage={true}
                    disableVideo={true}
                    disableQRCode={true}
                    disableTable={true}
                    disableAllLinks={true}
                    disablePhone={true}
                    agencyId={agencyId}
                    onAssetDelete={null}
                />
            </div>
            <CharacterLimitIndicator
                maxChars={PUSH_MAX_CHARS}
                inputStr={htmlToPlainText(currentPushBody || '')}
            />

            <div className="row mt-4">
                <div className="col-6">
                    <div className="file-form-label">
                        {getText('msg_d42713493ca8')}
                        <span
                            style={{
                                color: 'var(--iNGAGED-Destructive-Color)',
                                marginLeft: '4px',
                            }}
                        >
                            *
                        </span>
                    </div>
                    <Dropdown
                        options={destinationOptions}
                        defaultValue={
                            destinationOptions.find(
                                option => option.value === selectedField,
                            )?.label
                        }
                        onChange={handleDestinationChange}
                        disabled={isReadOnly}
                    />
                </div>

                {selectedField === 'website' && (
                    <div className="col-6">
                        <div className="file-form-label">
                            {getText('msg_d0517071aa37')}
                            <span
                                style={{
                                    color: 'var(--iNGAGED-Destructive-Color)',
                                    marginLeft: '4px',
                                }}
                            >
                                *
                            </span>
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            value={pushDestinationValue || ''}
                            onChange={event =>
                                onDestinationChange(
                                    pushDestinationTypeId,
                                    event.target.value,
                                )
                            }
                            disabled={isReadOnly}
                            style={{ height: '50px', borderRadius: '12px' }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationTemplatePushTab;
